import { toast } from "sonner";

import type {
  TournamentFinishedNotificationSchema,
  TournamentNewHighScoreNotificationSchema,
  TournamentRaceCompletedNotificationSchema,
  TournamentRaceFinishedNotificationSchema,
  TournamentSecondaryFinishedNotificationSchema,
  TournamentSecondaryRaceFinishedNotificationSchema,
} from "@/types";
import { isTournamentSubtypeHidden } from "@/campaigns/modules/tournament/unsupported-subtypes";
import { TournamentToast } from "@/components/notification/tournament-toast";
import { useAppStore } from "@/stores";
import { postPrizePayout } from "./host-events";
import { ownsInGameNotifications } from "./notifications";

/** The tournament types the widget displays: a result (a prize, or the final place without one)
 *  and the race / highest-win progress worth a toast. The chase ones (`tournament_chase_win_prize`,
 *  `tournament_reset_worst_prize`) are the chase module's — see `chase-notifications`. The
 *  tournament-mission ones (`tournament_secondary_{mission,task}_complete`,
 *  `tournament_reset_best_score`) belong to a subtype no surface shows, so the handler acks and
 *  drops them like any type it doesn't know. */
export type TournamentNotificationSchema =
  | TournamentFinishedNotificationSchema
  | TournamentRaceFinishedNotificationSchema
  | TournamentSecondaryFinishedNotificationSchema
  | TournamentSecondaryRaceFinishedNotificationSchema
  | TournamentRaceCompletedNotificationSchema
  | TournamentNewHighScoreNotificationSchema;

const TOURNAMENT_NOTIFICATION_TYPES = new Set([
  "tournament_finished",
  "tournament_race_finished",
  "tournament_secondary_finished",
  "tournament_secondary_race_finished",
  "tournament_race_completed",
  "tournament_new_high_score",
]);

export function isTournamentNotification(
  notification: { type?: string } & object
): notification is TournamentNotificationSchema {
  return TOURNAMENT_NOTIFICATION_TYPES.has(notification.type ?? "");
}

/** Narrowed by hand, as in mission-notifications: the generated schemas' optional `type` and
 *  index signature keep an inline check from narrowing the union. */
export function isTournamentPrizeNotification(
  notification: TournamentNotificationSchema
): notification is
  | TournamentFinishedNotificationSchema
  | TournamentRaceFinishedNotificationSchema {
  return (
    notification.type === "tournament_finished" ||
    notification.type === "tournament_race_finished"
  );
}

export function isNewHighScoreNotification(
  notification: TournamentNotificationSchema
): notification is TournamentNewHighScoreNotificationSchema {
  return notification.type === "tournament_new_high_score";
}

/**
 * Toast tournament results and progress, on the surface that owns the in-game notifications
 * (`ownsInGameNotifications`). Deduped against `seenNotificationIds` like the mission toasts. A
 * pending row is toasted whatever its age: it is acked only by this surface, so a row still here
 * is one the player has not seen — a tournament that ended while they were in the lobby is
 * announced when they open a game, as the legacy overlay did.
 */
export function showTournamentNotifications(
  notifications: TournamentNotificationSchema[]
): void {
  const state = useAppStore.getState();
  if (!ownsInGameNotifications(state)) return;
  const unseen = notifications.filter(
    (n) => !(n.id in state.seenNotificationIds)
  );
  if (unseen.length === 0) return;
  state.markNotificationsSeen(unseen.map((n) => n.id));

  const toasted: number[] = [];
  for (const notification of unseen) {
    // A campaign this surface hides (an unsupported subtype) is not announced either.
    const campaign = Object.values(state.campaignsById).find(
      (c) => c.id === notification.campaign_id
    );
    if (campaign && isTournamentSubtypeHidden(campaign.subtype, state.variant))
      continue;
    toasted.push(notification.campaign_id);
    toast.custom((id) => (
      <TournamentToast
        notification={notification}
        onDismiss={() => toast.dismiss(id)}
      />
    ));
    if (isTournamentPrizeNotification(notification)) {
      postPrizePayout(notification.id, { text: notification.prize_title });
    }
  }
  state.noteCampaignNews(toasted);
}
